import { useMemo } from 'react'
import { useDashboardGraph } from './useDashboardGraph'
import { useDashboardGraphEvents } from './useDashboardGraphEvents'
import {
  getDashboardTimezone,
  getDateHourInTimezone,
  getRangeFromDateAndHour,
} from '../../util/dateUtils'
import useDashboardListFilters from '../../util/useDashboardListFilters'
import { getDashboardQueryContext } from './dashboardHookUtils'
import { normalizeAlertSeverityCounts } from '../../util/severityCounts'

type AlertTotals = {
  low: number
  medium: number
  critical: number
}

const EMPTY_TOTALS: AlertTotals = {
  low: 0,
  medium: 0,
  critical: 0,
}

const ONE_HOUR_MS = 60 * 60 * 1000

const addSeverityCounts = (totals: AlertTotals, counts: unknown) => {
  const normalized = normalizeAlertSeverityCounts(counts)
  totals.low += normalized.low
  totals.medium += normalized.medium
  totals.critical += normalized.critical
}

type GraphSeriesEntry = {
  date: string
  alerts?: unknown
  night: Array<{ hour: number }>
  morning: Array<{ hour: number }>
  afternoon: Array<{ hour: number }>
  evening: Array<{ hour: number }>
}

type EntryAccumulator = (
  totals: AlertTotals,
  entry: GraphSeriesEntry,
  timezone: string,
  isInSelectedRange: (timestampMs: number) => boolean
) => void

/**
 * Sums a day-granularity entry, which carries its totals on the entry itself
 * and leaves the day-part arrays empty. Membership is anchored at the local day
 * start, the same boundary the graph's zoom ranges are built from.
 */
const addDayEntryTotals: EntryAccumulator = (totals, entry, timezone, isInSelectedRange) => {
  const { start } = getRangeFromDateAndHour(entry.date, 0, 24, timezone)
  if (!isInSelectedRange(start.getTime())) return
  addSeverityCounts(totals, entry.alerts)
}

/** Sums an hour-granularity entry across its day-part slices. */
const addHourlyEntryTotals: EntryAccumulator = (totals, entry, timezone, isInSelectedRange) => {
  const slices = [...entry.night, ...entry.morning, ...entry.afternoon, ...entry.evening]
  for (const slice of slices) {
    const { start } = getRangeFromDateAndHour(entry.date, slice.hour, 1, timezone)
    if (!isInSelectedRange(start.getTime())) continue
    addSeverityCounts(totals, slice)
  }
}

export const useDashboardAlertTotals = () => {
  const { filters } = useDashboardListFilters()
  const timezone = getDashboardTimezone()
  const {
    selectedRangeDateHour: { date: eventDate, hour: eventHour },
    resolvedDateRange: { fromMs: rangeFromMs, toMs: rangeToMs, source: rangeSource },
  } = getDashboardQueryContext(filters, timezone)
  const {
    data: graphData,
    isLoading: isGraphLoading,
    isFetching: isGraphFetching,
  } = useDashboardGraph()
  const graph = graphData?.graph
  const hasValidRange = rangeFromMs !== undefined && rangeToMs !== undefined
  const shouldUseEventTotals =
    hasValidRange && rangeSource === 'graph' && rangeToMs - rangeFromMs <= ONE_HOUR_MS
  const eventRangeDateHour = useMemo(() => {
    if (!shouldUseEventTotals || rangeFromMs === undefined) {
      return { date: eventDate, hour: eventHour }
    }
    const derived = getDateHourInTimezone(rangeFromMs, timezone)
    return {
      date: derived.date ?? eventDate,
      hour: derived.hour ?? eventHour,
    }
  }, [shouldUseEventTotals, rangeFromMs, timezone, eventDate, eventHour])

  const {
    data: graphEvents,
    isLoading: isEventsLoading,
    isFetching: isEventsFetching,
  } = useDashboardGraphEvents({
    enabled: shouldUseEventTotals,
    date: eventRangeDateHour.date,
    hour: eventRangeDateHour.hour,
  })

  const totals = useMemo<AlertTotals>(() => {
    const seriesByDate = graph?.seriesByDate ?? []
    const nextTotals = { ...EMPTY_TOTALS }
    const isInSelectedRange = (timestampMs: number) =>
      !hasValidRange || (timestampMs >= rangeFromMs && timestampMs <= rangeToMs)

    if (shouldUseEventTotals) {
      for (const event of graphEvents ?? []) {
        const timestampMs = new Date(event.timestamp).getTime()
        if (!isInSelectedRange(timestampMs)) continue
        addSeverityCounts(nextTotals, event.alerts)
      }
      return nextTotals
    }

    if (seriesByDate.length === 0) return EMPTY_TOTALS

    const addEntryTotals =
      graph?.granularity === 'day' ? addDayEntryTotals : addHourlyEntryTotals
    for (const entry of seriesByDate) {
      addEntryTotals(nextTotals, entry, timezone, isInSelectedRange)
    }

    return nextTotals
  }, [graph, graphEvents, hasValidRange, rangeFromMs, rangeToMs, shouldUseEventTotals, timezone])

  return {
    alertTotals: totals,
    isLoading:
      isGraphLoading ||
      isGraphFetching ||
      (shouldUseEventTotals && (isEventsLoading || isEventsFetching)),
  }
}
